// src/pages/Employees/components/EmployeePayslip/PayslipPreview.jsx
import React, { useEffect } from "react";
import { Row, Col, Typography, Divider } from "antd";
import dayjs from "dayjs";
import "./payslipprint.css";

const { Text } = Typography;

const formatCurrency = (value, currency = "AED") =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    minimumFractionDigits: 0,
  }).format(Number(value || 0));

const PayslipPreview = ({
  employee = {},
  currency = "AED",
  logoUrl = "",
  base64Logo= "",
  selectedYear,
  attendanceSummary = {},
  payDetails = [],
  showSignature = true,
}) => {
  const flightTicketData = payDetails?.find(
    (pd) => pd?.pay_type === "Flight Ticket Reimbursement"
  );
  const flightTicketAmount = flightTicketData?.pay_amount || 0;
  const flightTicketEnteredDate = flightTicketData?.created_date;

  const {
    maxWorkingDays = 0,
    presentDays = 0,
    casualLeaveDays = 0,
    sickLeaveDays = 0,
    unpaidLeaveDays = 0,
    shortCasualLeaveDays = 0,
    shortLeaveDays = 0,
    shortSickLeaveDays = 0,
    flexiLeaveDays = 0,
    emergencyLeaveDays = 0,
  } = attendanceSummary;

  let allowances = [];
    try {
      allowances = typeof employee.allowance_values === "string"
        ? JSON.parse(employee.allowance_values)
        : employee.allowance_values || [];
    } catch (e) {
      allowances = [];
    }
  
  const effectiveCasualLeaveDays = casualLeaveDays + 0.5 * shortCasualLeaveDays
  const effectiveSickLeaveDays = sickLeaveDays + 0.5 * shortSickLeaveDays
  const effectiveShortLeaveDays = effectiveCasualLeaveDays + effectiveSickLeaveDays + shortLeaveDays

  const grossMonthly = Number(employee.total_monthly_salary) ||
  allowances.reduce((acc, a) => acc + (Number(a.amount) || 0), 0);

  const grossPerDay = maxWorkingDays > 0 ? grossMonthly / maxWorkingDays: 0;
  const tenureYears = (
    dayjs().diff(dayjs(employee.joining_date), "day") / 365
  ).toFixed(2);

  const additionalPays = (payDetails || []).filter(
    (p) =>
      Number(p?.pay_amount) > 0 &&
      typeof p?.pay_type === "string" &&
      !p.pay_type.toLowerCase().includes("deduction")
  );
  const additionsTotal = additionalPays.reduce(
    (acc, p) => acc + Number(p.pay_amount),
    0
  );

  const deductionPays = (payDetails || []).filter(
    (p) =>
      Number(p?.pay_amount) > 0 &&
      typeof p?.pay_type === "string" &&
      p.pay_type.toLowerCase().includes("deduction")
  );
  const deductionsTotal = deductionPays.reduce(
    (acc, p) => acc + Number(p.pay_amount),
    0
  );

  const deductionDays =
  Number(unpaidLeaveDays) +
  Number(attendanceSummary?.absentDays || 0);

  const unpaidLeaveDeduction = deductionDays * grossPerDay;

  const shortLeaveDeduction =
  (attendanceSummary?.shortLeaveDays || 0 ) * 0.5 * grossPerDay;

  const totalAttendanceDeduction =
    unpaidLeaveDeduction + shortLeaveDeduction;

  const netPay =
    grossMonthly + additionsTotal - deductionsTotal - totalAttendanceDeduction;

  const renderRow = (label, value) => (
    <Row style={{ marginBottom: 4 }}>
      <Col span={12}>
        <Text strong>{label}</Text>
      </Col>
      <Col span={12}>
        <Text>: {value}</Text>
      </Col>
    </Row>
  );


  useEffect(() => {
  const style = document.createElement("style");
  style.id = "payslip-print-style";

  style.innerHTML = `
    @media print {
      @page {
        size: A4 landscape;
        margin: 8mm 10mm;
      }

      body {
        -webkit-print-color-adjust: exact !important;
        print-color-adjust: exact !important;
        background: #fff !important;
      }

      button,
      .no-print {
        display: none !important;
      }
    }
  `;

  document.head.appendChild(style);

  return () => {
    document.getElementById("payslip-print-style")?.remove();
  };
}, []);

  return (
    <div id="payslip-preview" className="payslip-root">
      {/* Header */}
      <Row justify="center" align="middle" style={{ position: "relative" }}>
        <div style={{ position: "absolute", left: 0 }}>
          {logoUrl && (
            <img src={logoUrl} alt="Company Logo" className="logo-url" style={{ height: 40 }} />
          )}
          {console.log("base64Logo prop from payslippreview: ", base64Logo)}
          {base64Logo && (
      <img 
        src={base64Logo} 
        alt="Company Logo" 
        className="logo-base64" 
        style={{ height: 40 }} 
      />
    )}
        </div>
        <div>
          <div className="payslip-title">PAY SLIP</div>
          <div className="payslip-month">
            {employee.payslip_month || "Month YYYY"}
          </div>
        </div>
      </Row>

      <Divider className="payslip-divider" />

      {/* Info section */}
      <Row className="payslip-section">
        <Col span={12} className="payslip-left">
          {renderRow("Name of Employee", employee.employee_name)}
          {renderRow(
            "Joining Date",
            employee.joining_date
              ? dayjs(employee.joining_date).format("DD-MM-YYYY")
              : "--"
          )}
          {renderRow("Designation", employee.designation_name)}
          {renderRow("Department", employee.department_name)}
          {renderRow("Bank", employee.bank_name)}
          {renderRow("IBAN No.", employee.iban_no)}
          {flightTicketAmount > 0 &&
            renderRow(
              "Ticket Entitlement Date",
              flightTicketEnteredDate
                ? dayjs(flightTicketEnteredDate).format("DD-MM-YYYY")
                : "--"
            )}
          {flightTicketAmount > 0 &&
            renderRow(
              `${selectedYear} Ticket Allowance`,
              formatCurrency(flightTicketAmount, currency)
            )}
        </Col>

        <Col span={12} className="payslip-right">
          {renderRow("Total Working Days", maxWorkingDays)}
          {renderRow("Actual Working Days Attended", presentDays)}
          {renderRow("Casual Leave Used (CL)", effectiveCasualLeaveDays)}
          {renderRow("Sick Leave Used (SL)", effectiveSickLeaveDays)}
          {renderRow("Flexi Leave Used (FL)", flexiLeaveDays)}
          {renderRow("Emergency Leave (Unpaid)", emergencyLeaveDays)}
          {renderRow("Unpaid Leave", unpaidLeaveDays)}
          {renderRow("Tenure in Years", tenureYears)}
        </Col>
      </Row>

        <Row className="payslip-salary">
    {/* LEFT COLUMN */}
    <Col span={12} className="salary-left">
      {/* Salary table content (same as yours) */}
      <Row className="salary-header">
        <Col span={6}></Col>
        <Col span={6}>GROSS<br /> MONTHLY</Col>
        <Col span={6}>GROSS<br /> PER DAY</Col>
        <Col span={6}>ACTUAL<br /> SALARY</Col>
      </Row>

      {Array.isArray(employee?.allowance_values)
      ? allowances?.map((a, idx) => (
        <Row key={idx} className="salary-row">
          <Col span={6}>
            {a.name.replace(/_/g, " ").length > 21
              ? a.name.replace(/_/g, " ").slice(0, 18) + "..."
              : a.name.replace(/_/g, " ")}
          </Col>
          <Col span={6}>{formatCurrency(a.amount, currency)}</Col>
          <Col span={6}>
            {formatCurrency(
              maxWorkingDays > 0 ? a.amount / maxWorkingDays : 0,
              currency
            )}
          </Col>

          <Col span={6} style={{ fontWeight: "bold" }}>
            {formatCurrency(a.amount, currency)}
          </Col>
        </Row>
      )): ' - ' }

      <Row className="salary-total">
        <Col span={18}>GROSS SALARY</Col>
        <Col span={6}>{formatCurrency(grossMonthly, currency)}</Col>
      </Row>
    </Col>

    {/* RIGHT COLUMN */}
    <Col span={12} className="salary-right">
      <div className="right-header" style={{ textAlign: "center" }}>
        <Text strong>Other Additions & Deductions</Text>
      </div>

      {/* Add Section */}
      <div className="add-section">
        <Text strong underline>Add:</Text>
        <div style={{ marginTop: 4, marginLeft: 12 }}>
          {(additionalPays || []).length > 0 ? (
            additionalPays.map((p, i) => (
              <Row key={i} gutter={4}>
                <Col span={12}><Text>{p.pay_type}</Text></Col>
                <Col span={12}><Text>: {formatCurrency(p.pay_amount, currency)}</Text></Col>
              </Row>
            ))
          ) : (
            <Row gutter={4}>
              <Col span={12}><Text>Others</Text></Col>
              <Col span={12}><Text>: {formatCurrency(0, currency)}</Text></Col>
            </Row>
          )}
        </div>
      </div>

      {/* Deduct Section */}
      <div className="deduct-section" style={{ marginTop: 10 }}>
        <Text strong underline>Deduct:</Text>
        <div style={{ marginTop: 4, marginLeft: 12 }}>
          {/* Shows the actual money subtracted from Net Pay for unpaid
              leave/absence and short leave — previously this section only
              showed the "Unpaid Leave" day count with no currency amount,
              so Net Pay dropped below Gross Salary with no visible
              explanation of how much was deducted or why. */}
          {deductionDays > 0 && (
            <Row gutter={4}>
              <Col span={12}>
                <Text>
                  Unpaid Leave / Absence ({deductionDays} day
                  {deductionDays !== 1 ? "s" : ""})
                </Text>
              </Col>
              <Col span={12}>
                <Text>: {formatCurrency(unpaidLeaveDeduction, currency)}</Text>
              </Col>
            </Row>
          )}

          {shortLeaveDeduction > 0 && (
            <Row gutter={4}>
              <Col span={12}><Text>Short Leave Deduction</Text></Col>
              <Col span={12}>
                <Text>: {formatCurrency(shortLeaveDeduction, currency)}</Text>
              </Col>
            </Row>
          )}

          {(deductionPays || []).length > 0 ? (
            deductionPays.map((p, i) => (
              <Row key={i} gutter={4}>
                <Col span={12}><Text>{p.pay_type}</Text></Col>
                <Col span={12}><Text>: {formatCurrency(p.pay_amount, currency)}</Text></Col>
              </Row>
            ))
          ) : (
            <Row gutter={4}>
              <Col span={12}><Text>Others</Text></Col>
              <Col span={12}><Text>: {formatCurrency(0, currency)}</Text></Col>
            </Row>
          )}
        </div>
      </div>

      {/* ✅ NET PAY (inside right column, like old UI) */}
      <Row className="netpay-row">
        <Col span={24}>
          <div className="netpay-box">
            <span>NET PAY</span>
            <span>{formatCurrency(netPay, currency)}</span>
          </div>
        </Col>
      </Row>
    </Col>
  </Row>



      {/* Footer */}
      <Row style={{ marginTop: 8 }}>
        <Col span={18}>
          <Text>
            {currency} {employee.amount_words || ""}
          </Text>
        </Col>
      </Row>

      {showSignature ? (
        <div className="signature">
          <Text>Employee's Signature:</Text>
          <div className="signature-line" />
        </div>
      ) : (
        <div className="auto-note">
          <Text>
            This is a computer-generated payslip and does not require a physical signature.
          </Text>
        </div>
      )}

    </div>
  );
};

export default PayslipPreview;
